import { lumi } from "@/lib/lumi"
import { assets } from "@/lib/assets"
import { useAuth } from "@/hooks/useAuth"
import { useCallback, useEffect, useMemo, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import toast from "react-hot-toast"

type OrderStatus = "pending" | "confirmed" | "shipped" | "cancelled"

type OrderRecord = {
  _id: string
  name?: string
  phone?: string
  email?: string
  quantity?: number
  transferDate?: string
  transferAmount?: number
  accountLastFive?: string
  recipientName?: string
  recipientPhone?: string
  storeName?: string
  storeAddress?: string
  note?: string
  adminNote?: string
  totalAmount?: number
  status?: OrderStatus | string
  createdAt?: string
  updatedAt?: string
}

const STATUS_OPTIONS: { value: OrderStatus; label: string }[] = [
  { value: "pending", label: "待核款" },
  { value: "confirmed", label: "已確認" },
  { value: "shipped", label: "已出貨" },
  { value: "cancelled", label: "已取消" },
]

function statusLabel(status?: string) {
  return STATUS_OPTIONS.find((item) => item.value === status)?.label || status || "待核款"
}

function statusClass(status?: string) {
  if (status === "confirmed") return "bg-[#dce8df] text-[#2f5a3c]"
  if (status === "shipped") return "bg-[#d7e3ec] text-[#2b4456]"
  if (status === "cancelled") return "bg-[#efe4e0] text-[#7a4a3d]"
  return "bg-[#efe8d8] text-[#6b5428]"
}

function formatMoney(value?: number) {
  return `NT$ ${Number(value || 0).toLocaleString("zh-TW")}`
}

function formatTime(value?: string) {
  if (!value) return "—"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString("zh-TW", { hour12: false })
}

function AdminOrders() {
  const navigate = useNavigate()
  const { user, isAuthenticated, isAdmin } = useAuth()
  const [orders, setOrders] = useState<OrderRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [keyword, setKeyword] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [selectedId, setSelectedId] = useState("")
  const [draftStatus, setDraftStatus] = useState<OrderStatus>("pending")
  const [draftNote, setDraftNote] = useState("")
  const [saving, setSaving] = useState(false)

  const loadOrders = useCallback(async () => {
    setLoading(true)
    try {
      const result = await lumi.entities.orders.list({
        sort: { createdAt: -1 },
        limit: 500,
      })
      const list = (result?.list || []) as OrderRecord[]
      setOrders(list)
      setSelectedId((prev) => prev || list[0]?._id || "")
    } catch (error: unknown) {
      const err = error as { message?: string }
      toast.error(err?.message || "無法讀取訂單，請確認管理員權限")
      setOrders([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/admin/login", { replace: true })
      return
    }
    if (!isAdmin) return
    loadOrders()
  }, [isAuthenticated, isAdmin, loadOrders, navigate])

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase()
    return orders.filter((order) => {
      const status = order.status || "pending"
      if (statusFilter !== "all" && status !== statusFilter) return false
      if (!q) return true
      const haystack = [
        order.name,
        order.phone,
        order.email,
        order.recipientName,
        order.storeName,
        order.accountLastFive,
      ]
        .join(" ")
        .toLowerCase()
      return haystack.includes(q)
    })
  }, [orders, keyword, statusFilter])

  const selected = filtered.find((order) => order._id === selectedId) || filtered[0] || orders.find((order) => order._id === selectedId)

  useEffect(() => {
    if (!selected) return
    setDraftStatus((selected.status as OrderStatus) || "pending")
    setDraftNote(selected.adminNote || "")
  }, [selected?._id, selected?.status, selected?.adminNote])

  const counts = useMemo(() => {
    return {
      all: orders.length,
      pending: orders.filter((item) => (item.status || "pending") === "pending").length,
      confirmed: orders.filter((item) => item.status === "confirmed").length,
      shipped: orders.filter((item) => item.status === "shipped").length,
      cancelled: orders.filter((item) => item.status === "cancelled").length,
    }
  }, [orders])

  const handleLogout = async () => {
    await lumi.auth.signOut()
    window.location.href = "/admin/login"
  }

  const handleSave = async () => {
    if (!selected?._id) return
    setSaving(true)
    try {
      const updatedAt = new Date().toISOString()
      await lumi.entities.orders.update(selected._id, {
        status: draftStatus,
        adminNote: draftNote.trim(),
        updatedAt,
      })
      toast.success("訂單已更新")
      await loadOrders()
    } catch (error: unknown) {
      const err = error as { message?: string }
      toast.error(err?.message || "更新失敗")
    } finally {
      setSaving(false)
    }
  }

  const handleExport = () => {
    const header = [
      "建立時間",
      "狀態",
      "訂購人",
      "手機",
      "Email",
      "數量",
      "總金額",
      "匯款日期",
      "匯款金額",
      "帳號末五碼",
      "收件人",
      "收件手機",
      "門市",
      "門市地址",
      "備註",
      "後台備註",
    ]
    const rows = filtered.map((order) => [
      formatTime(order.createdAt),
      statusLabel(order.status),
      order.name || "",
      order.phone || "",
      order.email || "",
      String(order.quantity || 0),
      String(order.totalAmount || 0),
      order.transferDate || "",
      String(order.transferAmount || 0),
      order.accountLastFive || "",
      order.recipientName || "",
      order.recipientPhone || "",
      order.storeName || "",
      order.storeAddress || "",
      order.note || "",
      order.adminNote || "",
    ])
    const csv = [header, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n")
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = "jiangmeiqi-orders.csv"
    link.click()
    URL.revokeObjectURL(url)
  }

  if (!isAuthenticated) return null

  if (!isAdmin) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#eef3f6] px-6">
        <div className="max-w-md border border-[#d5dde3] bg-[#f7f3ec] px-8 py-10 text-center">
          <h1 className="font-serif-tc text-2xl tracking-[0.16em] text-slate-800">權限不足</h1>
          <p className="mt-4 text-[13px] leading-7 text-slate-500">
            目前帳號 {user?.email || ""} 不是管理員，無法查看訂單後台。
          </p>
          <button
            type="button"
            onClick={handleLogout}
            className="mt-8 h-11 w-full bg-[#2b4456] text-[12px] tracking-[0.24em] text-white"
          >
            登出
          </button>
        </div>
      </main>
    )
  }

  return (
    <main className="min-h-screen bg-[#e8eef2] text-slate-700">
      <header className="border-b border-[#d5dde3] bg-[#f7f3ec]">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-5 py-4 md:px-8">
          <div className="flex items-center gap-4">
            <img src={assets.logo} alt="江美琪限定香氛" className="h-8 w-auto" />
            <div>
              <p className="font-display text-[11px] tracking-[0.32em] text-slate-400">ORDER LEDGER</p>
              <h1 className="font-serif-tc text-xl tracking-[0.12em] text-slate-800">訂單管理</h1>
            </div>
          </div>
          <div className="flex items-center gap-3 text-[12px]">
            <span className="text-slate-400">{user?.email || ""}</span>
            <Link to="/" className="tracking-[0.12em] text-slate-500 hover:text-slate-800">
              官網
            </Link>
            <button type="button" onClick={handleLogout} className="tracking-[0.12em] text-slate-500 hover:text-slate-800">
              登出
            </button>
          </div>
        </div>
      </header>

      <section className="mx-auto max-w-7xl px-5 py-6 md:px-8">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {[
            { key: "all", label: "全部訂單", value: counts.all },
            { key: "pending", label: "待核款", value: counts.pending },
            { key: "confirmed", label: "已確認", value: counts.confirmed },
            { key: "shipped", label: "已出貨", value: counts.shipped },
            { key: "cancelled", label: "已取消", value: counts.cancelled },
          ].map((card) => (
            <button
              key={card.key}
              type="button"
              onClick={() => setStatusFilter(card.key)}
              className={`border px-4 py-4 text-left ${
                statusFilter === card.key ? "border-[#2b4456] bg-[#2b4456] text-white" : "border-[#d5dde3] bg-white"
              }`}
            >
              <p className="text-[11px] tracking-[0.18em] opacity-70">{card.label}</p>
              <p className="mt-2 font-display text-3xl">{card.value}</p>
            </button>
          ))}
        </div>

        <div className="mt-6 flex flex-col gap-3 lg:flex-row lg:items-center">
          <input
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="搜尋姓名、手機、Email、門市"
            className="h-11 flex-1 border border-[#d5dde3] bg-white px-4 text-[13px] outline-none focus:border-slate-500"
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={loadOrders}
              disabled={loading}
              className="h-11 px-4 border border-[#2b4456] text-[12px] tracking-[0.16em] text-[#2b4456] disabled:opacity-60"
            >
              {loading ? "重新整理中…" : "重新整理"}
            </button>
            <button
              type="button"
              onClick={handleExport}
              className="h-11 px-4 bg-[#2b4456] text-[12px] tracking-[0.16em] text-white"
            >
              匯出 CSV
            </button>
          </div>
        </div>

        <div className="mt-6 grid gap-6 xl:grid-cols-[1.4fr_0.9fr]">
          <div className="overflow-hidden border border-[#d5dde3] bg-white">
            {loading ? (
              <p className="px-5 py-16 text-center text-[13px] text-slate-400">讀取訂單中…</p>
            ) : filtered.length === 0 ? (
              <p className="px-5 py-16 text-center text-[13px] leading-7 text-slate-400">
                尚無符合條件的訂單。
                <br />
                前台送出的訂單會直接寫入此後台。
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-[13px]">
                  <thead className="bg-[#f7f3ec] text-[11px] tracking-[0.14em] text-slate-500">
                    <tr>
                      <th className="px-4 py-3 font-medium">時間</th>
                      <th className="px-4 py-3 font-medium">訂購人</th>
                      <th className="px-4 py-3 font-medium">數量</th>
                      <th className="px-4 py-3 font-medium">金額</th>
                      <th className="px-4 py-3 font-medium">門市</th>
                      <th className="px-4 py-3 font-medium">狀態</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((order) => {
                      const active = order._id === selected?._id
                      return (
                        <tr
                          key={order._id}
                          onClick={() => setSelectedId(order._id)}
                          className={`cursor-pointer border-t border-[#edf1f4] ${active ? "bg-[#eef3f6]" : "hover:bg-[#f8fafb]"}`}
                        >
                          <td className="whitespace-nowrap px-4 py-3 text-slate-500">{formatTime(order.createdAt)}</td>
                          <td className="px-4 py-3">
                            <p className="text-slate-800">{order.name || "—"}</p>
                            <p className="text-[12px] text-slate-400">{order.phone || ""}</p>
                          </td>
                          <td className="px-4 py-3">{order.quantity || 0}</td>
                          <td className="px-4 py-3">{formatMoney(order.totalAmount)}</td>
                          <td className="max-w-[180px] truncate px-4 py-3">{order.storeName || "—"}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-block px-2 py-1 text-[11px] tracking-[0.08em] ${statusClass(order.status)}`}>
                              {statusLabel(order.status)}
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <aside className="border border-[#d5dde3] bg-[#f7f3ec] p-5">
            {selected ? (
              <div className="space-y-5">
                <div>
                  <p className="font-display text-[11px] tracking-[0.28em] text-slate-400">ORDER DETAIL</p>
                  <h2 className="mt-1 font-serif-tc text-2xl text-slate-800">{selected.name || "未命名訂單"}</h2>
                  <p className="mt-1 text-[12px] text-slate-400">{formatTime(selected.createdAt)}</p>
                </div>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-[13px]">
                  <div>
                    <dt className="text-[11px] tracking-[0.12em] text-slate-400">手機</dt>
                    <dd>{selected.phone || "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] tracking-[0.12em] text-slate-400">Email</dt>
                    <dd className="break-all">{selected.email || "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] tracking-[0.12em] text-slate-400">數量 / 總額</dt>
                    <dd>
                      {selected.quantity || 0} 組 · {formatMoney(selected.totalAmount)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[11px] tracking-[0.12em] text-slate-400">匯款</dt>
                    <dd>
                      {selected.transferDate || "—"} / {formatMoney(selected.transferAmount)} / {selected.accountLastFive || "—"}
                    </dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="text-[11px] tracking-[0.12em] text-slate-400">7-ELEVEN</dt>
                    <dd className="leading-6">
                      {selected.recipientName || "—"} {selected.recipientPhone || ""}
                      <br />
                      {selected.storeName || "—"}
                      <br />
                      {selected.storeAddress || ""}
                    </dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="text-[11px] tracking-[0.12em] text-slate-400">顧客備註</dt>
                    <dd className="leading-6">{selected.note || "無"}</dd>
                  </div>
                </dl>
                <label className="block">
                  <span className="mb-2 block text-[11px] tracking-[0.12em] text-slate-400">訂單狀態</span>
                  <select
                    value={draftStatus}
                    onChange={(event) => setDraftStatus(event.target.value as OrderStatus)}
                    className="h-11 w-full border border-slate-300 bg-white px-3 text-[13px] outline-none"
                  >
                    {STATUS_OPTIONS.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="mb-2 block text-[11px] tracking-[0.12em] text-slate-400">後台備註</span>
                  <textarea
                    value={draftNote}
                    onChange={(event) => setDraftNote(event.target.value)}
                    rows={4}
                    className="w-full border border-slate-300 bg-white px-3 py-3 text-[13px] outline-none"
                  />
                </label>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="h-11 w-full bg-[#2b4456] text-[12px] tracking-[0.24em] text-white disabled:opacity-60"
                >
                  {saving ? "儲存中…" : "儲存變更"}
                </button>
              </div>
            ) : (
              <p className="py-12 text-center text-[13px] text-slate-400">請選擇一筆訂單</p>
            )}
          </aside>
        </div>
      </section>
    </main>
  )
}

export default AdminOrders
